/**
 * Production error logging for React Query
 * Captures, categorizes, and logs query/mutation errors with context
 */

import { QueryKey } from '@tanstack/react-query';

export type ErrorType = 
  | 'network'
  | 'authentication'
  | 'authorization'
  | 'validation'
  | 'server'
  | 'timeout'
  | 'aborted'
  | 'unknown';

export interface ErrorLogData {
  timestamp: string;
  queryKey: QueryKey;
  errorType: ErrorType;
  statusCode?: number;
  message: string;
  stack?: string;
  userContext?: {
    userId?: string;
    orgId?: string;
    userAgent?: string;
  };
  environment: string;
  url?: string;
  method?: string;
  retryCount?: number;
}

/**
 * Categorize error based on status code and error type
 */
export function categorizeError(error: any): ErrorType {
  // Check if request was aborted
  if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
    return 'aborted';
  }

  // Check for timeout
  if (error?.name === 'TimeoutError' || error?.message?.toLowerCase().includes('timeout')) {
    return 'timeout';
  }

  // Network errors
  if (!error?.status && (error?.message?.includes('fetch') || error?.message?.includes('network'))) {
    return 'network';
  }

  // Status code based categorization
  const status = error?.status || error?.response?.status;
  
  if (status === 401) return 'authentication';
  if (status === 403) return 'authorization';
  if (status >= 400 && status < 500) return 'validation';
  if (status >= 500) return 'server';

  return 'unknown';
}

/**
 * Determine if error should be logged
 * Filters out expected errors and noise
 */
export function shouldLogError(error: any, errorType: ErrorType): boolean {
  // Don't log in development
  if (process.env.NODE_ENV !== 'production') {
    return false;
  }

  // Don't log aborted requests (user navigation)
  if (errorType === 'aborted') {
    return false;
  }

  // Don't log 401 during initial auth (Clerk session refresh)
  if (errorType === 'authentication' && error?.message?.includes('session refresh')) {
    return false;
  }

  // Don't log cancelled requests
  if (error?.message?.includes('cancelled') || error?.message?.includes('canceled')) {
    return false;
  }

  return true;
}

/**
 * Determine if error is critical and requires admin notification
 */
export function shouldNotifyAdmin(errorType: ErrorType, statusCode?: number): boolean {
  // Server errors are critical
  if (errorType === 'server') return true;
  
  // Repeated authorization failures might indicate security issue
  if (errorType === 'authorization' && statusCode === 403) return true;
  
  return false;
}

/**
 * Sanitize error data to remove sensitive information
 */
export function sanitizeErrorData(data: any): any {
  if (!data) return data;
  
  const sensitiveKeys = [
    'password',
    'token',
    'secret',
    'apiKey',
    'creditCard',
    'ssn',
    'authorization'
  ];

  if (typeof data === 'string') {
    // Redact potential tokens in strings
    return data.replace(/Bearer\s+[\w-]+/gi, 'Bearer [REDACTED]');
  }

  if (typeof data === 'object') {
    const sanitized: any = Array.isArray(data) ? [] : {};
    
    for (const key in data) {
      const lowerKey = key.toLowerCase();
      
      // Check if key contains sensitive information
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else if (key === 'email') {
        // Partial mask emails
        const email = data[key];
        if (typeof email === 'string' && email.includes('@')) {
          const [local, domain] = email.split('@');
          sanitized[key] = `${local.substring(0, 2)}***@${domain}`;
        } else {
          sanitized[key] = email;
        }
      } else {
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeErrorData(data[key]);
      }
    }
    
    return sanitized;
  }

  return data;
}

/**
 * Extract query key string for logging
 */
export function stringifyQueryKey(queryKey: QueryKey): string {
  try {
    if (Array.isArray(queryKey)) {
      return queryKey.map(k => 
        typeof k === 'object' ? JSON.stringify(k) : String(k)
      ).join(' > ');
    }
    return String(queryKey);
  } catch {
    return 'unknown-query-key';
  }
}

/**
 * Main error logging function
 */
export async function logQueryError(
  queryKey: QueryKey,
  error: any,
  context?: {
    userId?: string;
    orgId?: string;
    retryCount?: number;
  }
): Promise<void> {
  const errorType = categorizeError(error);
  
  // Check if we should log this error
  if (!shouldLogError(error, errorType)) {
    return;
  }

  const errorData: ErrorLogData = {
    timestamp: new Date().toISOString(),
    queryKey,
    errorType,
    statusCode: error?.status || error?.response?.status,
    message: sanitizeErrorData(error?.message || 'Unknown error'),
    stack: process.env.NODE_ENV === 'production' ? undefined : error?.stack,
    userContext: {
      userId: context?.userId,
      orgId: context?.orgId,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
    },
    environment: process.env.NODE_ENV || 'unknown',
    url: error?.config?.url || error?.url,
    method: error?.config?.method || error?.method,
    retryCount: context?.retryCount,
  };

  // Log to console in development with formatting
  if (process.env.NODE_ENV === 'development') {
    console.error(
      `🔴 Query Error [${errorType}]:`,
      stringifyQueryKey(queryKey),
      '\nStatus:', errorData.statusCode,
      '\nMessage:', errorData.message,
      '\nRetry:', context?.retryCount || 0
    );
    return;
  }

  // In production, send to logging endpoint
  try {
    // Use sendBeacon for non-blocking logging
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(errorData)], { type: 'application/json' });
      navigator.sendBeacon('/api/logs/client-errors', blob);
    } else {
      // Fallback to fetch with no-await (fire and forget)
      fetch('/api/logs/client-errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData),
        keepalive: true, // Ensure request completes even if page unloads
      }).catch(() => {
        // Silent fail - don't want error logging to cause more errors
      });
    }

    // Check if admin notification is needed
    if (shouldNotifyAdmin(errorType, errorData.statusCode)) {
      // Import toast dynamically to avoid client/server issues
      if (typeof window !== 'undefined') {
        const { toast } = await import('sonner');
        toast.error(
          'A critical error occurred. Our team has been notified.',
          { duration: 5000 }
        );
      }
    }
  } catch (loggingError) {
    // Silent fail - don't want error logging to cause more errors
    console.error('Failed to log error:', loggingError);
  }
}

/**
 * Batch error logger to reduce API calls
 */
class ErrorBatcher {
  private batch: ErrorLogData[] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 20;
  private readonly BATCH_DELAY = 5000; // 5 seconds

  add(errorData: ErrorLogData): void {
    this.batch.push(errorData);

    // Send immediately if batch is full
    if (this.batch.length >= this.BATCH_SIZE) {
      this.flush();
      return;
    }

    // Otherwise, schedule batch send
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => this.flush(), this.BATCH_DELAY);
    }
  }

  private async flush(): Promise<void> {
    if (this.batch.length === 0) return;

    const toSend = [...this.batch];
    this.batch = [];
    
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    try {
      await fetch('/api/logs/client-errors/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errors: toSend }),
        keepalive: true,
      });
    } catch {
      // Silent fail
    }
  }
}

// Singleton instance for batching
export const errorBatcher = new ErrorBatcher();
